import OrderMethodIcon from '../../assets/icons/order-methods/OrderMethodIcon';
import TabBar from './TabBar';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import { FC, ReactNode, useMemo } from 'react';
import { ORDER_METHODS } from '../../services/exports/Constants';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import classnames from 'classnames';
import useTheme from '../../hooks/ui/useTheme';
import { CompanyDetailsInterface } from '../../services/exports/Interfaces';
import collect from 'collect.js';

interface Props {
  orderMethod: ORDER_METHODS;
  company?: CompanyDetailsInterface;
  className?: string;
  tabBarItemClassName?: string;
  tabBarItemActiveClassName?: string;
  onClick?: (orderMethod: ORDER_METHODS) => void;
  forcedOrderMethods?: ORDER_METHODS[];
}

const OrderMethodsTabBar: FC<Props> = ({
  orderMethod,
  onClick,
  className,
  company,
  forcedOrderMethods,
  tabBarItemClassName,
  tabBarItemActiveClassName,
}): ReactNode => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Navigation:OrderMethodsTabBar' });
  const { visibleOrderMethods, renderOrderMethodBasedCopy } = useOrderMethodsStatus(company);
  const orderMethods: ORDER_METHODS[] = useMemo(
    () =>
      collect(visibleOrderMethods)
        .when(!!forcedOrderMethods?.length, (collection) => collection.concat(forcedOrderMethods))
        .unique()
        .toArray(),
    [visibleOrderMethods, forcedOrderMethods],
  );
  const { toggleOrderMethodModal } = useAppMethods();
  const theme = useTheme();

  return (
    <TabBar
      items={orderMethods.map((item) => ({
        label: <p className="text-sm">{renderOrderMethodBasedCopy(item, t, 'order_methods')}</p>,
        value: item,
        icon: (
          <OrderMethodIcon
            orderMethod={item}
            className={classnames('h-[16px] w-[16px]', {
              'w-[20px]': item === ORDER_METHODS.DELIVERY,
            })}
            color={item === orderMethod ? theme.color.brand.primary : theme.color.brand.icon.color}
          />
        ),
        className: classnames('px-none lg:px-2xl', tabBarItemClassName),
        activeClassName: tabBarItemActiveClassName,
      }))}
      value={orderMethod}
      className={className ?? '!bg-brand-background-primary dark:!bg-black-600 shadow-section dark:shadow-none'}
      onClick={(value) => (onClick ? onClick(value) : toggleOrderMethodModal({ openModal: true, orderMethod: value }))}
    />
  );
};

export default OrderMethodsTabBar;
